import { Flex, HelpIndicator, RadioButton } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import {
  ValueCell,
  type DocumentData,
} from "~/pages/Compliance/ProposedChangesModal/ValueCell";
import { type SelectionSource } from "./constants";
import { useSelection } from "./SelectionContext";
import * as styles from "./styles_vanilla.css";
import type { ComparisonRow } from "./types";

export type RadioValueCellProps = {
  row: ComparisonRow;
  source: SelectionSource;
  value: string;
  docs: DocumentData[] | undefined;
  profileId: number;
  onDocumentClick?: (docId: number, profileId: number) => void;
  readonly?: boolean;
};

const MATCH_LABELS: Record<SelectionSource, string> = {
  current: "Same as current",
  recentlyPublished: "Same as recently published",
  proposed: "Same as proposed",
};

export const RadioValueCell = ({
  row,
  source,
  value,
  docs,
  profileId,
  onDocumentClick,
  readonly = false,
}: RadioValueCellProps) => {
  const { getSelection, setSelection } = useSelection();
  const selection = getSelection(row.selectionGroupKey);
  const canRender = source !== "recentlyPublished" || row.hasPublished;
  const matches = row.dimmedMatches[source];
  const dimmed = matches !== undefined;

  const content = (
    <Flex alignItems="center" gap={Sizes.SM} className={styles.fullWidth}>
      <Flex alignItems="center" className={styles.radioSlot}>
        {row.renderRadio && canRender && (
          <RadioButton
            name={row.selectionGroupKey}
            value={source}
            checked={selection === source}
            disabled={dimmed || readonly}
            onChange={() => setSelection(row.selectionGroupKey, source)}
            data-testid={`review-radio-${row.selectionGroupKey}-${source}`}
          />
        )}
      </Flex>
      <div className={styles.valueContent}>
        <ValueCell
          value={value}
          docs={docs}
          isDocument={row.isDocument}
          onDocumentClick={dimmed ? undefined : (docId) => onDocumentClick?.(docId, profileId)}
          dimmed={dimmed}
        />
      </div>
    </Flex>
  );

  if (!dimmed) return content;

  return (
    <HelpIndicator
      id={`review-dimmed-${row.selectionGroupKey}-${source}`}
      placement="top"
      content={MATCH_LABELS[matches]}
    >
      {content}
    </HelpIndicator>
  );
};
